import Ajax from './ajax';
import { cloneDeep } from 'lodash';

export const CARGO_PROVIDERS_GET_PENDING = 'cargopricing/CARGO_PROVIDERS_GET_PENDING';
export const CARGO_PROVIDERS_RETRIEVED = 'cargopricing/CARGO_PROVIDERS_RETRIEVED';
export const CARGO_PROVIDER_SAVED = 'cargopricing/CARGO_PROVIDER_SAVED';

export const CARGO_PRODUCTS_RETRIEVED = 'cargopricing/CARGO_PRODUCTS_RETRIEVED';
export const CARGO_PRODUCT_SAVED = 'cargopricing/CARGO_PRODUCT_SAVED';
export const CARGO_PRODUCT_DELETED = 'cargopricing/CARGO_PRODUCT_DELETED';

export const CARGO_PURCHASE_GROUPS_RETRIEVED = 'cargopricing/CARGO_PURCHASE_GROUPS_RETRIEVED';
export const CARGO_PURCHASE_GROUP_SAVED = 'cargopricing/CARGO_PURCHASE_GROUP_SAVED';
export const CARGO_PURCHASE_GROUP_DELETED = 'cargopricing/CARGO_PURCHASE_GROUP_DELETED';

export const CARGO_SALES_GROUPS_RETRIEVED = 'cargopricing/CARGO_SALES_GROUPS_RETRIEVED';
export const CARGO_SALES_GROUP_SAVED = 'cargopricing/CARGO_SALES_GROUP_SAVED';
export const CARGO_SALES_GROUP_DELETED = 'cargopricing/CARGO_SALES_GROUP_DELETED';

export const CARGO_SALES_GROUP_CONTRACT_LINKS_RETRIEVED = 'cargopricing/CARGO_SALES_GROUP_CONTRACT_LINKS_RETRIEVED';
export const CARGO_ALL_SALES_GROUP_CONTRACT_LINKS_RETRIEVED =
    'cargopricing/CARGO_ALL_SALES_GROUP_CONTRACT_LINKS_RETRIEVED';
export const CARGO_SALES_GROUP_CONTRACT_LINK_ADDED = 'cargopricing/CARGO_SALES_GROUP_CONTRACT_LINK_ADDED';
export const CARGO_SALES_GROUP_CONTRACT_LINK_REMOVED = 'cargopricing/CARGO_SALES_GROUP_CONTRACT_LINK_REMOVED';

export const CARGO_PROVIDER_PRODUCTS_RETRIEVED = 'cargopricing/CARGO_PROVIDER_PRODUCTS_RETRIEVED';
export const CARGO_PROVIDER_PRODUCT_SAVED = 'cargopricing/CARGO_PROVIDER_PRODUCT_SAVED';
export const CARGO_PROVIDER_PRODUCT_DELETED = 'cargopricing/CARGO_PROVIDER_PRODUCT_DELETED';

const initialState = {
    providers: null,
    products: null,
    purchaseGroups: null,
    salesGroups: null,
    // keyed by cargoSalesGroupId: array of { clusterId, cargoSalesGroupId, contractNumberId }
    salesGroupContractLinks: {},
    // explicit cargo provider ↔ product associations, each optionally tagged with a deliveryMethodId
    providerProducts: null,
    loading: false,
};

export default (state = initialState, action) => {
    switch (action.type) {
        case CARGO_PROVIDERS_GET_PENDING:
            return { ...state, loading: true };

        case CARGO_PROVIDERS_RETRIEVED:
            return { ...state, loading: false, providers: action.providers };

        case CARGO_PROVIDER_SAVED: {
            const list = cloneDeep(state.providers || []);
            const replaceId = action.oldId || action.item.clusterId;
            const idx = list.findIndex((p) => p.clusterId === replaceId);
            if (idx >= 0) list[idx] = action.item;
            else list.push(action.item);
            return { ...state, providers: list };
        }

        case CARGO_PRODUCTS_RETRIEVED:
            return { ...state, products: action.products };

        case CARGO_PRODUCT_SAVED: {
            const list = cloneDeep(state.products || []);
            const idx = list.findIndex((p) => p.clusterId === action.item.clusterId);
            if (idx >= 0) list[idx] = action.item;
            else list.push(action.item);
            return { ...state, products: list };
        }
        case CARGO_PRODUCT_DELETED: {
            const list = (state.products || []).filter((p) => p.clusterId !== action.id);
            return { ...state, products: list };
        }

        case CARGO_PURCHASE_GROUPS_RETRIEVED:
            return { ...state, purchaseGroups: action.purchaseGroups };

        case CARGO_PURCHASE_GROUP_SAVED: {
            const list = cloneDeep(state.purchaseGroups || []);
            const idx = list.findIndex((g) => g.clusterId === action.item.clusterId);
            if (idx >= 0) list[idx] = action.item;
            else list.push(action.item);
            return { ...state, purchaseGroups: list };
        }
        case CARGO_PURCHASE_GROUP_DELETED: {
            const list = (state.purchaseGroups || []).filter((g) => g.clusterId !== action.id);
            return { ...state, purchaseGroups: list };
        }

        case CARGO_SALES_GROUPS_RETRIEVED:
            return { ...state, salesGroups: action.salesGroups };

        case CARGO_SALES_GROUP_SAVED: {
            const list = cloneDeep(state.salesGroups || []);
            const replaceId = action.oldId || action.item.clusterId;
            const idx = list.findIndex((g) => g.clusterId === replaceId);
            if (idx >= 0) list[idx] = action.item;
            else list.push(action.item);
            // The backend only allows one default sales group per provider+product combination
            // and silently clears any previous default when a new one is saved. Mirror that here
            // so other groups in the list stop showing a stale "default" check mark without a
            // full page refresh.
            if (action.item.isDefault) {
                list.forEach((g) => {
                    if (
                        g.clusterId !== action.item.clusterId &&
                        g.cargoProviderId === action.item.cargoProviderId &&
                        g.cargoProductId === action.item.cargoProductId
                    ) {
                        g.isDefault = false;
                    }
                });
            }
            // The backend re-points existing contract links from the old (now archived)
            // clusterId to the new one as part of the immutable re-versioning (see
            // UpsertSalesGroup/UpdateSalesGroupReferences). Mirror that locally: move any cached
            // links keyed by the old id over to the new id so they don't appear to vanish.
            const updatedLinks = { ...state.salesGroupContractLinks };
            if (action.oldId && updatedLinks[action.oldId] && !updatedLinks[action.item.clusterId]) {
                updatedLinks[action.item.clusterId] = updatedLinks[action.oldId].map((l) => ({
                    ...l,
                    cargoSalesGroupId: action.item.clusterId,
                }));
                delete updatedLinks[action.oldId];
            }
            return { ...state, salesGroups: list, salesGroupContractLinks: updatedLinks };
        }
        case CARGO_SALES_GROUP_DELETED: {
            const list = (state.salesGroups || []).filter((g) => g.clusterId !== action.id);
            return { ...state, salesGroups: list };
        }

        case CARGO_SALES_GROUP_CONTRACT_LINKS_RETRIEVED:
            return {
                ...state,
                salesGroupContractLinks: { ...state.salesGroupContractLinks, [action.salesGroupId]: action.links },
            };

        case CARGO_ALL_SALES_GROUP_CONTRACT_LINKS_RETRIEVED: {
            const grouped = {};
            (Array.isArray(action.links) ? action.links : []).forEach((link) => {
                const key = link.cargoSalesGroupId;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(link);
            });
            return { ...state, salesGroupContractLinks: grouped };
        }

        case CARGO_SALES_GROUP_CONTRACT_LINK_ADDED: {
            const existing = state.salesGroupContractLinks[action.salesGroupId] || [];
            return {
                ...state,
                salesGroupContractLinks: {
                    ...state.salesGroupContractLinks,
                    [action.salesGroupId]: [...existing, action.link],
                },
            };
        }
        case CARGO_SALES_GROUP_CONTRACT_LINK_REMOVED: {
            const existing = state.salesGroupContractLinks[action.salesGroupId] || [];
            return {
                ...state,
                salesGroupContractLinks: {
                    ...state.salesGroupContractLinks,
                    [action.salesGroupId]: existing.filter((l) => l.clusterId !== action.linkId),
                },
            };
        }

        case CARGO_PROVIDER_PRODUCTS_RETRIEVED:
            return { ...state, providerProducts: action.providerProducts };

        case CARGO_PROVIDER_PRODUCT_SAVED: {
            const list = cloneDeep(state.providerProducts || []);
            const idx = list.findIndex((pp) => pp.clusterId === action.item.clusterId);
            if (idx >= 0) list[idx] = action.item;
            else list.push(action.item);
            return { ...state, providerProducts: list };
        }
        case CARGO_PROVIDER_PRODUCT_DELETED: {
            const list = (state.providerProducts || []).filter((pp) => pp.clusterId !== action.id);
            return { ...state, providerProducts: list };
        }

        default:
            return state;
    }
};

export const getCargoProviders = () => async (dispatch) => {
    dispatch({ type: CARGO_PROVIDERS_GET_PENDING });
    const resp = await Ajax.get('api/cargopricing/provider');
    dispatch({ type: CARGO_PROVIDERS_RETRIEVED, providers: resp.data });
};

export const saveCargoProvider = (provider) => async (dispatch) => {
    const oldId = provider.clusterId > 0 ? provider.clusterId : null;
    const resp =
        provider.clusterId > 0
            ? await Ajax.put(`api/cargopricing/provider/${provider.clusterId}`, provider)
            : await Ajax.post('api/cargopricing/provider', provider);
    dispatch({ type: CARGO_PROVIDER_SAVED, item: resp.data, oldId });
};

export const getCargoProducts = () => async (dispatch) => {
    const resp = await Ajax.get('api/cargopricing/product');
    dispatch({ type: CARGO_PRODUCTS_RETRIEVED, products: resp.data });
};

export const saveCargoProduct = (product) => async (dispatch) => {
    const resp =
        product.clusterId > 0
            ? await Ajax.put(`api/cargopricing/product/${product.clusterId}`, product)
            : await Ajax.post('api/cargopricing/product', product);
    dispatch({ type: CARGO_PRODUCT_SAVED, item: resp.data });
};

export const deleteCargoProduct = (id) => async (dispatch) => {
    await Ajax.delete(`api/cargopricing/product/${id}`);
    dispatch({ type: CARGO_PRODUCT_DELETED, id });
};

export const getCargoPurchaseGroups = () => async (dispatch) => {
    const resp = await Ajax.get('api/cargopricing/purchasegroup');
    dispatch({ type: CARGO_PURCHASE_GROUPS_RETRIEVED, purchaseGroups: resp.data });
};

export const saveCargoPurchaseGroup = (group) => async (dispatch) => {
    const resp =
        group.clusterId > 0
            ? await Ajax.put(`api/cargopricing/purchasegroup/${group.clusterId}`, group)
            : await Ajax.post('api/cargopricing/purchasegroup', group);
    dispatch({ type: CARGO_PURCHASE_GROUP_SAVED, item: resp.data });
};

export const deleteCargoPurchaseGroup = (id) => async (dispatch) => {
    await Ajax.delete(`api/cargopricing/purchasegroup/${id}`);
    dispatch({ type: CARGO_PURCHASE_GROUP_DELETED, id });
};

export const getCargoSalesGroups = () => async (dispatch) => {
    const resp = await Ajax.get('api/cargopricing/salesgroup');
    dispatch({ type: CARGO_SALES_GROUPS_RETRIEVED, salesGroups: resp.data });
};

export const saveCargoSalesGroup = (group) => async (dispatch) => {
    const oldId = group.clusterId > 0 ? group.clusterId : null;
    const resp =
        group.clusterId > 0
            ? await Ajax.put(`api/cargopricing/salesgroup/${group.clusterId}`, group)
            : await Ajax.post('api/cargopricing/salesgroup', group);
    dispatch({ type: CARGO_SALES_GROUP_SAVED, item: resp.data, oldId });
};

export const deleteCargoSalesGroup = (id) => async (dispatch) => {
    await Ajax.delete(`api/cargopricing/salesgroup/${id}`);
    dispatch({ type: CARGO_SALES_GROUP_DELETED, id });
};

export const fetchSalesGroupContractLinks = (salesGroupId) => async (dispatch) => {
    const resp = await Ajax.get(`api/cargopricing/salesgroupcontract/salesgroup/${salesGroupId}`);
    dispatch({ type: CARGO_SALES_GROUP_CONTRACT_LINKS_RETRIEVED, salesGroupId, links: resp.data });
};

export const fetchAllSalesGroupContractLinks = () => async (dispatch) => {
    const resp = await Ajax.get('api/cargopricing/salesgroupcontract/all');
    dispatch({ type: CARGO_ALL_SALES_GROUP_CONTRACT_LINKS_RETRIEVED, links: resp.data });
};

export const addSalesGroupContractLink = (salesGroupId, contractId) => async (dispatch) => {
    const resp = await Ajax.post('api/cargopricing/salesgroupcontract', {
        contractNumberId: contractId,
        cargoSalesGroupId: salesGroupId,
    });
    dispatch({ type: CARGO_SALES_GROUP_CONTRACT_LINK_ADDED, salesGroupId, link: resp.data });
};

export const removeSalesGroupContractLink = (linkId, salesGroupId) => async (dispatch) => {
    await Ajax.delete(`api/cargopricing/salesgroupcontract/${linkId}`);
    dispatch({ type: CARGO_SALES_GROUP_CONTRACT_LINK_REMOVED, salesGroupId, linkId });
};

export const getCargoProviderProducts = () => async (dispatch) => {
    const resp = await Ajax.get('api/cargopricing/providerproduct');
    dispatch({ type: CARGO_PROVIDER_PRODUCTS_RETRIEVED, providerProducts: resp.data });
};

export const saveCargoProviderProduct = (item) => async (dispatch) => {
    const resp =
        item.clusterId > 0
            ? await Ajax.put(`api/cargopricing/providerproduct/${item.clusterId}`, item)
            : await Ajax.post('api/cargopricing/providerproduct', item);
    dispatch({ type: CARGO_PROVIDER_PRODUCT_SAVED, item: resp.data });
};

export const deleteCargoProviderProduct = (id) => async (dispatch) => {
    await Ajax.delete(`api/cargopricing/providerproduct/${id}`);
    dispatch({ type: CARGO_PROVIDER_PRODUCT_DELETED, id });
};
